import React from 'react';
import { useSelector } from 'react-redux';

import { Button } from '@mantine/core';
import { type SortingState, flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowUpDown, RotateCcw } from 'lucide-react';

import { ExportTableColumns } from './ExportTableColumns';
import { ColumnVisibilityMenu } from '../../antibody-analysis/components/common/ColumnVisibilityMenu';
import { SearchPill } from '../../antibody-analysis/components/common/SearchPill';
import { useChainWidths } from '../../antibody-analysis/context/ChainWidthContext';
import { selectChainType, selectHighlightedRegions, selectShowFWR, selectShowLiabilities, selectShowSHM } from '../../store/slices/antibodyAnalysis.slice';
import type { ExportRow } from '../types';

const s = {
  scroll: 'h-full overflow-auto bg-[#e2f2ff]',
  table: 'border-collapse table-fixed min-w-full w-max',
  thead: 'sticky top-0 z-10 bg-[#e2f2ff]',
  headerCell: 'text-left text-[13px] font-medium text-[#3d3d3d] px-2 py-1.5 border-b border-border/40 whitespace-nowrap',
  headerSortable: 'flex items-center gap-1 cursor-pointer select-none hover:text-foreground/80',
  sortIcon: 'w-3 h-3 opacity-40 translate-y-px',
  // Rows carry the same tint as the header — on Export summary nearly every row is selected,
  // so Hit selection's darker selected shade would just darken the whole table.
  row: 'border-b border-border/30 hover:bg-[#d6edff]',
  bodyCell: 'px-2 py-1 text-xs align-middle bg-[#e2f2ff]',
  empty: 'px-4 py-10 text-sm text-[#8a938e]',
  toolbar: 'flex items-center gap-2 px-4 py-2',
  toolbarActions: 'flex items-center gap-2',
  resetIcon: 'w-3.5 h-3.5',
  wrap: 'flex flex-col h-full min-h-0',
  spacerCell: 'bg-[#e2f2ff] border-b border-border/40',
} as const;

/** Fields the search box matches against — the text columns a user would actually search by. */
function rowMatchesSearch(row: ExportRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) {
    return true;
  }
  return [row.nodeLabel, row.cdrh3, row.cdrl3, row.vjGeneHeavy, row.vjGeneLight, String(row.clonotypeId)].some((field) =>
    (field ?? '').toLowerCase().includes(q),
  );
}

interface ExportTableProps {
  rows: ExportRow[];
  isSelected: (nodeId: number) => boolean;
  onToggle: (nodeId: number) => void;
  onToggleAll: () => void;
}

/**
 * The flat export table — one row per node.
 */
export function ExportTable({ rows, isSelected, onToggle, onToggleAll }: ExportTableProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [searchQuery, setSearchQuery] = React.useState('');
  const chainType = useSelector(selectChainType);
  const showFWR = useSelector(selectShowFWR);
  const highlightedRegions = useSelector(selectHighlightedRegions);
  const showLiabilities = useSelector(selectShowLiabilities);
  const showShms = useSelector(selectShowSHM);
  const { heavy: heavyChainWidth, light: lightChainWidth } = useChainWidths();

  const selectedCount = rows.filter((row) => isSelected(row.nodeId)).length;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const handleRegionClick = React.useCallback(() => {}, []);

  const columns = React.useMemo(
    () =>
      ExportTableColumns({
        isSelected,
        onToggle,
        onToggleAll,
        allSelected,
        someSelected,
        chainType,
        searchQuery,
        showFWR,
        highlightedRegions,
        showLiabilities,
        showShms,
        heavyChainWidth,
        lightChainWidth,
        onRegionClick: handleRegionClick,
      }),
    [
      isSelected,
      onToggle,
      onToggleAll,
      allSelected,
      someSelected,
      chainType,
      searchQuery,
      showFWR,
      highlightedRegions,
      showLiabilities,
      showShms,
      heavyChainWidth,
      lightChainWidth,
      handleRegionClick,
    ],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, globalFilter: searchQuery },
    onSortingChange: setSorting,
    globalFilterFn: (row, _columnId, filterValue) => rowMatchesSearch(row.original, filterValue as string),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const handleReset = React.useCallback(() => {
    setSearchQuery('');
    setSorting([]);
    table.resetColumnVisibility();
  }, [table]);

  const tableRows = table.getRowModel().rows;

  // Each row renders two full sequence viewers (~230 residue spans), so rendering every row
  // eagerly is what makes a few hundred nodes freeze the page. Same padding-row approach as
  // OuterParentClonotypeTable.
  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 28,
    overscan: 12,
  });
  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom = virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : 0;
  const colCount = table.getVisibleLeafColumns().length;

  if (rows.length === 0) {
    return <p className={s.empty}>Nothing selected yet.</p>;
  }

  const toolbar = (
    <div className={s.toolbar}>
      <SearchPill value={searchQuery} onChange={setSearchQuery} placeholder="Search sequences" />
      <div className={s.toolbarActions}>
        <Button
          variant="subtle"
          size="compact-xs"
          c="#3d3d3d"
          styles={{ section: { marginInlineEnd: 4 } }}
          leftSection={<RotateCcw className={s.resetIcon} />}
          onClick={handleReset}
        >
          Reset
        </Button>
        <ColumnVisibilityMenu table={table} />
      </div>
    </div>
  );

  return (
    <div className={s.wrap}>
      {toolbar}
      {tableRows.length === 0 ? (
        <p className={s.empty}>No sequences match “{searchQuery}”.</p>
      ) : (
        <div className={s.scroll} ref={scrollRef}>
          <table className={s.table}>
            <thead className={s.thead}>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} className={s.headerCell} style={{ width: header.getSize() }}>
                      {header.column.getCanSort() ? (
                        <span
                          className={s.headerSortable}
                          role="button"
                          tabIndex={0}
                          onClick={header.column.getToggleSortingHandler()}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              header.column.toggleSorting();
                            }
                          }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <ArrowUpDown className={s.sortIcon} />
                        </span>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  ))}
                  <th aria-hidden className={s.spacerCell} />
                </tr>
              ))}
            </thead>
            <tbody>
              {paddingTop > 0 && (
                <tr>
                  <td colSpan={colCount + 1} style={{ height: paddingTop, padding: 0, border: 0 }} />
                </tr>
              )}
              {virtualItems.map((virtualRow) => {
                const row = tableRows[virtualRow.index];
                return (
                  <tr key={row.id} data-index={virtualRow.index} ref={rowVirtualizer.measureElement} className={s.row}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className={s.bodyCell} style={{ width: cell.column.getSize() }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                    <td aria-hidden className={s.spacerCell} />
                  </tr>
                );
              })}
              {paddingBottom > 0 && (
                <tr>
                  <td colSpan={colCount + 1} style={{ height: paddingBottom, padding: 0, border: 0 }} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
